import { useDebouncedValue } from "@mantine/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { isApiError } from "../../api/errors";
import { quoteJob, useStartJob } from "../../api/generated/endpoints/generation/generation";
import type { Job, JobQuoteRequest, JobRequest } from "../../api/generated/model";
import { useIdempotencyKey } from "../idempotency";

/** The quote runs this long after a form stops changing: the composer's and the tool forms' (3.3). */
export const QUOTE_DEBOUNCE_MS = 500;

export interface PricedStartOptions {
  teamId: string;
  brandId: string;
  /** What to price, or undefined while the form cannot be priced. */
  quote: JobQuoteRequest | undefined;
  /** What a start sends, or undefined while the form cannot start. */
  request: JobRequest | undefined;
  onStarted: (job: Job) => void;
}

/**
 * A form's price and start, the one way the generation forms run them (G1): the quote prices
 * `quote` half a second after it settles, cached a minute; the start sends `request` with the
 * price's maximum and an Idempotency-Key, and `QUOTE_CHANGED` prices it again. `lastPriced` is the
 * figure to show: the one before stays while the next is made, as live's run quote keeps it
 * (`useTemplateRunQuote`'s `keepPreviousData`), and none once the form can't be priced or the price
 * is refused; a start only ever sends its own body's price. The Sounds forms
 * (through `useSoundJob`) and the AI Actors composer use it; the Studio's composer keeps its own
 * (it prices its draft).
 */
export function usePricedStart({ teamId, brandId, quote, request, onStarted }: PricedStartOptions) {
  const body = quote === undefined ? undefined : JSON.stringify(quote);
  const [settled] = useDebouncedValue(body, QUOTE_DEBOUNCE_MS);
  const priced = useQuery({
    queryKey: ["quote", teamId, brandId, settled],
    queryFn: ({ signal }) => quoteJob(teamId, brandId, JSON.parse(settled ?? "{}"), { signal }),
    enabled: settled !== undefined,
    retry: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const idempotency = useIdempotencyKey();
  const start = useStartJob({ mutation: { onSuccess: onStarted } });
  const shown = settled === body && !priced.isPlaceholderData ? priced.data : undefined;
  const startJob = () => {
    if (request === undefined || shown === undefined) return;
    const data = { ...request, expected_quote_max: shown.credits_max };
    const key = idempotency.keyFor(data);
    start.mutate(
      { teamId, brandId, data, headers: { "Idempotency-Key": key } },
      {
        onError: (error) => {
          idempotency.failed(key, data, error);
          if (isApiError(error, "QUOTE_CHANGED")) void priced.refetch();
        },
      },
    );
  };
  return {
    priced: shown,
    lastPriced: body === undefined ? undefined : priced.data,
    pricing: priced.isFetching || settled !== body,
    quoteError: priced.isError && settled === body ? priced.error : null,
    start,
    startJob,
    canStart: request !== undefined && shown !== undefined,
  };
}

export type PricedStart = ReturnType<typeof usePricedStart>;
